import React, { useState, useEffect } from "react";
import { authFetch, apiUrl } from "../../utils/api";

/**
 * OrcaConnectCard — Optimus on your desktop (Orca), synced to the same
 * brain. Download the macOS installer, run it, and paste a one-time sync
 * key so Orca's chats + files land in the same shared brain as web + Mako.
 *
 * Backend: POST /api/devices/link (surface=orca) mints a device key shown
 * ONCE; GET /api/devices lists; POST /api/devices/:id/revoke. Behind the
 * shared_brain_sync flag (device endpoints 404 when off → card hides).
 */
export default function OrcaConnectCard() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [freshKey, setFreshKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false); // collapse the setup steps by default (save real estate)

  const loadDevices = async () => {
    try {
      const r = await authFetch(apiUrl("/api/devices"));
      if (r.status === 404) {
        setEnabled(false);
        return;
      }
      const d = await r.json();
      setDevices((d.devices || []).filter((x) => x.surface === "orca" && !x.revoked));
    } catch (_) {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const generateKey = async () => {
    setBusy(true);
    setFreshKey("");
    try {
      const r = await authFetch(apiUrl("/api/devices/link"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceName: "Orca (desktop)", surface: "orca" }),
      });
      const d = await r.json();
      if (d.key) setFreshKey(d.key);
      await loadDevices();
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    setBusy(true);
    try {
      await authFetch(apiUrl(`/api/devices/${id}/revoke`), { method: "POST" });
      await loadDevices();
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  return (
    <div
      className="hf-panel"
      style={{
        marginBottom: "40px",
        padding: "24px",
        borderRadius: "12px",
        border: "1px solid var(--border-primary)",
        background:
          "var(--bg-tertiary)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <img
          src="/images/characters/orca-monitor.webp"
          alt="Orca on desktop"
          width="56"
          height="47"
          style={{ flexShrink: 0 }}
        />
        <div>
          <h3 style={{ margin: 0, fontSize: "18px" }}>
            Want Optimus on your desktop? Meet Orca.
          </h3>
          <p
            style={{
              margin: "4px 0 0",
              color: "var(--text-secondary)",
              fontSize: "14px",
            }}
          >
            A voice-first orchestrator that drives Claude agents right on your
            Mac — deep flow, local files, real horsepower. Sync it up and its
            chats and files show up here alongside web and Mako. One brain,
            everywhere.
          </p>
        </div>
      </div>

      <div style={{ marginTop: "18px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {/* Canonical download — the same notarized pkg marbles.link/orcamac
            serves. The OS used to host its own copy in public/, which went
            stale: it was still the 0.8MB source-only 0.9.0 build (the one
            that failed on every non-dev Mac) while 0.9.1 shipped. One URL,
            one truth, no more stale copies. */}
        <a
          href="https://orcaorchestrator.com/orca-installer-mac.pkg"
          download
          className="hf-press hf-focus"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "var(--accent-primary)",
            color: "var(--text-on-accent)",
            border: "none",
            borderRadius: "8px",
            padding: "10px 18px",
            fontWeight: 600,
            fontSize: "14px",
            textDecoration: "none",
          }}
        >
          ⬇️ Download Orca for macOS
        </a>
        <button
          onClick={generateKey}
          disabled={busy}
          className="hf-press hf-focus"
          style={{
            background: "transparent",
            border: "1px solid var(--border-primary)",
            color: "var(--accent-primary)",
            borderRadius: "8px",
            padding: "10px 18px",
            fontWeight: 600,
            fontSize: "14px",
            cursor: busy ? "default" : "pointer",
          }}
        >
          {busy ? "…" : "Generate sync key"}
        </button>
      </div>

      {freshKey && (
        <div
          style={{
            marginTop: "14px",
            padding: "14px",
            borderRadius: "10px",
            background: "var(--bg-success-subtle)",
            border: "1px solid var(--border-success)",
          }}
        >
          <p style={{ margin: "0 0 8px", fontSize: "13px", color: "var(--accent-success)" }}>
            Your sync key — copy it now, it won't be shown again. In Orca, run{" "}
            <code>optimus login</code> and paste it:
          </p>
          <code
            className="select-all"
            style={{
              display: "block",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "var(--bg-primary)",
              color: "var(--accent-primary)",
              fontSize: "13px",
              wordBreak: "break-all",
            }}
          >
            {freshKey}
          </code>
        </div>
      )}

      {/* Collapsible setup: caret to minimize once you're set up */}
      <button
        onClick={() => setStepsOpen((s) => !s)}
        style={{
          marginTop: "18px",
          background: "transparent",
          border: "none",
          color: "var(--accent-primary)",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          padding: 0,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <span style={{ transform: stepsOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▸</span>
        {stepsOpen ? "Hide setup walkthrough & steps" : "Watch the walkthrough & setup steps"}
      </button>

      {stepsOpen && (
      <>
      {/* 40-second setup walkthrough */}
      <div
        style={{
          position: "relative",
          paddingBottom: "56.25%",
          height: 0,
          borderRadius: "12px",
          overflow: "hidden",
          marginTop: "14px",
          border: "1px solid transparent",
        }}
      >
        <iframe
          src="https://player.vimeo.com/video/1210880360?title=0&byline=0&portrait=0"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title="Orca setup walkthrough"
        />
      </div>

      <div style={{ marginTop: "18px" }}>
        <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: "0 0 12px" }}>
          Four quick steps. Copy each command — no typing, no formatting fuss.
        </p>

        <Step n="1" title="Download & open Orca">
          Grab the installer above and open it (right-click → Open if macOS warns
          you). Orca will help you finish the rest.
        </Step>

        <Step n="2" title="Install the Claude Code engine">
          Orca drives Claude Code under the hood. Install it once — pick your OS:
          <div style={{ marginTop: "8px", fontSize: "12px", color: "var(--text-tertiary)", fontWeight: 600 }}>macOS — Terminal</div>
          <Cmd cmd="curl -fsSL https://claude.ai/install.sh | bash" />
          <small style={{ color: "var(--text-secondary)" }}>
            If <code>claude</code> isn't found afterwards: run{" "}
            <code>{`echo 'export PATH="$HOME/.local/bin:$PATH"' >> ~/.zshrc`}</code>, then
            close and reopen Terminal.
          </small>
          <div style={{ marginTop: "10px", fontSize: "12px", color: "var(--text-tertiary)", fontWeight: 600 }}>Windows — three prep steps, then the install</div>
          <small style={{ color: "var(--text-secondary)", display: "block" }}>
            <strong>1. Path.</strong> Windows key → type "environment variables" → <em>Edit the system
            environment variables</em> → <strong>Environment Variables…</strong>. In the <strong>top</strong> box
            click the row named <strong>Path</strong> → <strong>Edit…</strong> → <strong>New</strong> → type exactly{" "}
            <code>%USERPROFILE%\.local\bin</code> → OK on every window. You are editing the
            <strong> existing</strong> Path row — never create a new variable, or the install is never found.
            <br />
            <strong>2. PowerShell as Administrator.</strong> Windows key → type "PowerShell" → right-click
            <strong> Windows PowerShell</strong> → <strong>Run as administrator</strong> → Yes. The title bar
            must say <em>Administrator: Windows PowerShell</em>; if it doesn't, close it and reopen it the right way.
            <br />
            <strong>3. Turn on paste.</strong> Right-click that window's title bar → <strong>Properties</strong> →{" "}
            <strong>Options</strong> → tick <strong>Use Ctrl+Shift+C/V as Copy/Paste</strong> → OK. Ctrl+Shift+V now pastes.
          </small>
          <Cmd cmd="irm https://claude.ai/install.ps1 | iex" />
          <small style={{ color: "var(--text-secondary)" }}>
            If the next line shows <code>&gt;&gt;</code> instead of the install starting, press Backspace once on
            that line, then Enter. When it finishes run <code>claude --version</code>; if Windows says "not
            recognized", close PowerShell and reopen it as Administrator — the Path change only applies to
            windows opened after it.
          </small>
        </Step>

        <Step n="3" title="Let Orca work without interruptions">
          So Orca can actually get things done instead of stopping to ask
          permission at every step, start it in flow mode:
          <Cmd cmd="claude --dangerously-skip-permissions" />
          <small style={{ color: "var(--text-secondary)" }}>
            Sounds scary, isn't — it just means Orca doesn't pause for a yes/no on
            every action inside your own machine. Orca sets this up for you.
          </small>
        </Step>

        <Step n="4" title="Bring your own gas (connect your Claude)">
          This is the important one. Get your Claude OAuth token so Orca runs on
          your Pro/Max account — you own the usage, no limits, no metering:
          <Cmd cmd="claude setup-token" />
          <small style={{ color: "var(--text-secondary)" }}>
            Paste the token into Optimus OS → Settings → Integrations (Claude Max).
            Then generate a sync key above — Orca's setup wizard offers the link
            step itself (or run <code>optimus login</code> anytime) — and your
            chats and files sync to the shared brain. <code>optimus status</code>{" "}
            confirms it. Done.
          </small>
        </Step>
      </div>
      </>
      )}

      {!loading && devices.length > 0 && (
        <div style={{ marginTop: "12px" }}>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: "0 0 8px" }}>
            Linked Orca devices:
          </p>
          {devices.map((d) => (
            <div
              key={d.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: "8px",
                background: "var(--bg-secondary)",
                marginBottom: "6px",
                fontSize: "13px",
              }}
            >
              <span>
                🖥️ {d.device_name}{" "}
                <span style={{ color: "var(--text-secondary)" }}>({d.key_prefix}…)</span>
              </span>
              <button
                onClick={() => revoke(d.id)}
                disabled={busy}
                style={{
                  background: "transparent",
                  border: "1px solid var(--border-primary)",
                  color: "var(--text-secondary)",
                  borderRadius: "8px",
                  padding: "4px 10px",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A numbered setup step. */
function Step({ n, title, children }) {
  return (
    <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
      <div
        style={{
          flexShrink: 0,
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          background: "var(--accent-primary)",
          color: "var(--text-on-accent)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "13px",
          fontWeight: 700,
        }}
      >
        {n}
      </div>
      <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: 1.6 }}>
        <div style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: "4px" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

/** A single copyable command block — one command, its own box, copy button. */
function Cmd({ cmd }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* clipboard blocked — user can still select the text */
    }
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        margin: "8px 0",
        padding: "8px 10px",
        borderRadius: "8px",
        background: "var(--bg-primary)",
        border: "1px solid transparent",
      }}
    >
      <code
        className="select-all"
        style={{ flex: 1, color: "var(--accent-primary)", fontSize: "13px", wordBreak: "break-all" }}
      >
        {cmd}
      </code>
      <button
        onClick={copy}
        style={{
          flexShrink: 0,
          background: copied ? "var(--accent-success)" : "transparent",
          border: "1px solid var(--border-primary)",
          color: copied ? "var(--text-on-accent)" : "var(--accent-primary)",
          borderRadius: "8px",
          padding: "4px 10px",
          fontSize: "12px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
